import { useEffect, useState } from 'react';
import { DocumentoLegalModal } from './DocumentoLegalModal';

/**
 * Banner não-bloqueante para versão MINOR de documento legal (REQ-012 RN-17 /
 * AB#63). Aparece quando há documento ATIVO `nivel_versao=MINOR` publicado
 * nos últimos 7 dias e o usuário ainda não dispensou esse banner.
 *
 * Persistência: ao clicar "Entendi", grava `aceiteMinorDispensado:<id>` no
 * localStorage — sem ida ao servidor (sai do escopo da story).
 *
 * Renderiza por tipo (TERMOS_USO/PRIVACIDADE). Para múltiplos MINORs ativos,
 * mostra o primeiro não dispensado.
 */

const TIPOS: Array<'TERMOS_USO' | 'PRIVACIDADE'> = ['TERMOS_USO', 'PRIVACIDADE'];
const TIPO_LABEL: Record<string, string> = {
  TERMOS_USO: 'Termos de Uso',
  PRIVACIDADE: 'Política de Privacidade',
};
const SETE_DIAS_MS = 7 * 24 * 60 * 60 * 1000;

interface DocAtivo {
  found: boolean;
  id?: number;
  tipo?: 'TERMOS_USO' | 'PRIVACIDADE';
  versao?: string;
  nivel_versao?: 'PATCH' | 'MINOR' | 'MAJOR';
  publicado_em?: string | null;
}

function jaDispensado(id: number): boolean {
  return localStorage.getItem(`aceiteMinorDispensado:${id}`) === '1';
}
function marcarDispensado(id: number): void {
  localStorage.setItem(`aceiteMinorDispensado:${id}`, '1');
}

export function AtualizacaoMinorBanner(): JSX.Element | null {
  const [ativo, setAtivo] = useState<DocAtivo | null>(null);
  const [modalAberto, setModalAberto] = useState(false);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const API_URL = `${import.meta.env.VITE_API_URL ?? 'http://localhost:8000'}/api`;
      for (const tipo of TIPOS) {
        try {
          const res = await fetch(`${API_URL}/documentos-legais/publico/${tipo}`);
          if (!res.ok) continue;
          const data = (await res.json()) as DocAtivo;
          if (!data.found || data.nivel_versao !== 'MINOR' || !data.publicado_em || !data.id) continue;
          if (Date.now() - new Date(data.publicado_em).getTime() > SETE_DIAS_MS) continue;
          if (jaDispensado(data.id)) continue;
          if (!cancelado) {
            setAtivo(data);
            return;
          }
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  if (!ativo) return null;

  return (
    <>
      <div
        role="status"
        className="sticky top-0 z-30 flex w-full flex-wrap items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-6 py-2.5 text-sm text-amber-900 shadow-sm"
      >
        <p className="font-medium">
          Atualizamos os {TIPO_LABEL[ativo.tipo!]}{' '}
          <button
            type="button"
            onClick={() => setModalAberto(true)}
            className="underline underline-offset-2 hover:text-amber-700"
          >
            Ver o que mudou
          </button>
        </p>
        <button
          type="button"
          onClick={() => {
            marcarDispensado(ativo.id!);
            setAtivo(null);
          }}
          className="rounded-md border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100"
        >
          Entendi
        </button>
      </div>
      <DocumentoLegalModal
        tipo={ativo.tipo!}
        aberto={modalAberto}
        onFechar={() => setModalAberto(false)}
      />
    </>
  );
}
